import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketStatus } from '../../../models/Support/SupportEnums';
import { useUpdateTicketStatusMutation } from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { TicketChannelBadge } from '../../components/supportShared/TicketChannelBadge';
import { CheckGlyph, EyeGlyph, EyeOffGlyph, ForwardGlyph, PersonSwapGlyph, ReopenGlyph } from './inboxIcons';
import { SnoozeTicketPopover } from './SnoozeTicketPopover';
import { styles } from './styles';
import { getComplexityInfo } from './supportTicketPresentation';
import { TicketSummaryPopover } from './TicketSummaryPopover';
import { useSupportTicketActivityVisibility } from './useSupportTicketActivityVisibility';

interface TicketTopBarProps {
	ticket: ISupportTicket;
	/** Called when the user selects Forward. */
	onForward?: () => void;
	/** Called when the agent wants to change the customer/contact attached to the ticket. */
	onChangeCustomer?: () => void;
	/** Called after the ticket is closed or snoozed — parent advances to the next ticket. */
	onTicketLeftView?: () => void;
}

export const TicketTopBar: React.FC<TicketTopBarProps> = ({
	ticket,
	onForward,
	onChangeCustomer,
	onTicketLeftView,
}) => {
	const [showReopenConfirm, setShowReopenConfirm] = React.useState(false);
	const [showTicketActivity, setShowTicketActivity] = useSupportTicketActivityVisibility();

	const updateStatus = useUpdateTicketStatusMutation(ticket.id);
	const activityToggleLabel = showTicketActivity ? 'Hide activity' : 'Show activity';
	const customerActionLabel = ticket.contactEmail?.trim() ? 'Change customer' : 'Add customer';
	const isClosed = ticket.status === TicketStatus.Closed;
	const canSnooze =
		ticket.status === TicketStatus.Active ||
		ticket.status === TicketStatus.Pending ||
		ticket.status === TicketStatus.Snoozed;
	// Toasted rather than held inline: a rejection that stays on screen after the rep fixes its cause
	// (e.g. sets the missing category) reads as a second, fresh failure.
	const feedback = (failMessage: string, onSuccess?: () => void) => ({
		onSuccess: () => onSuccess?.(),
		onError: (error: unknown) => toast.error(getApiErrorMessage(error, failMessage)),
	});

	return (
		<>
			<div className={css(styles.topBarInline)}>
				<div className={css(styles.topBarLeft)}>
					<div className={css(styles.topBarTitleRow)}>
						<span className={css(styles.ticketNumber)}>#{ticket.number}</span>
						<span className={css(styles.ticketSubject)}>{ticket.subject}</span>
						<TicketChannelBadge channel={ticket.channel} />
						{ticket.complexity != null &&
							(() => {
								const info = getComplexityInfo(ticket.complexity);
								return (
									<span
										className={css(styles.aiScoreBadge, info.style)}
										title={ticket.complexityReasoning || ticket.aiReasoning || undefined}
									>
										{info.label} ({ticket.complexity})
									</span>
								);
							})()}
						<TicketSummaryPopover ticket={ticket} />
					</div>
				</div>
				<div className={css(styles.topBarActions)}>
					<Button
						variant='primary'
						size='default'
						onClick={() => {
							if (isClosed) {
								setShowReopenConfirm(true);
							} else {
								updateStatus.mutate(TicketStatus.Closed, feedback('Failed to close ticket.', onTicketLeftView));
							}
						}}
						isLoading={updateStatus.isPending}
						title={isClosed ? 'Reopen' : 'Close'}
						styles={[styles.actionPrimaryBtn]}
					>
						{isClosed ? <ReopenGlyph size={16} /> : <CheckGlyph size={16} />}
						{isClosed ? 'Reopen' : 'Close'}
					</Button>
					{canSnooze && <SnoozeTicketPopover ticket={ticket} onSnoozed={onTicketLeftView} />}
					<Button
						type='button'
						variant='outline'
						size='icon'
						onClick={() => setShowTicketActivity(show => !show)}
						title={activityToggleLabel}
						aria-label={activityToggleLabel}
						aria-pressed={showTicketActivity}
						styles={[styles.actionIconBtn, ...(showTicketActivity ? [] : [styles.actionIconBtnActive])]}
					>
						{showTicketActivity ? <EyeGlyph size={18} /> : <EyeOffGlyph size={18} />}
					</Button>
					<Button
						variant='outline'
						size='icon'
						onClick={() => onForward?.()}
						title='Forward (f)'
						aria-label='Forward'
						styles={[styles.actionIconBtn]}
					>
						<ForwardGlyph size={18} />
					</Button>
					<Button
						variant='outline'
						size='icon'
						onClick={() => onChangeCustomer?.()}
						title={customerActionLabel}
						aria-label={customerActionLabel}
						styles={[styles.actionIconBtn]}
					>
						<PersonSwapGlyph size={18} />
					</Button>
				</div>
			</div>
			{showReopenConfirm && (
				<ConfirmModal
					title='Reopen Ticket'
					message='Reopen this ticket and move it back to Active?'
					confirmLabel='Reopen'
					onConfirm={() => {
						setShowReopenConfirm(false);
						updateStatus.mutate(TicketStatus.Active, feedback('Failed to reopen ticket.'));
					}}
					onCancel={() => setShowReopenConfirm(false)}
				/>
			)}
		</>
	);
};
